import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RadioItem from '../../../../components/RadioItem';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { exchange } from '../../../../utilities/constants';
import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../../../utilities/formatHelpers';
import { validateNumbers } from '../../../../utilities/formValidation';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import FocusableInputArea from '../../../Accounts/partials/FocusableInputArea';
import { MoreCollateralPaymentMethod, WalletsTypes } from '../../constants';
import { cryptodraftScreens } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';

const AddCollateral = () => {
  const { t: translate } = useTranslation();

  const { state, openScreen, updatePgwRequestParams } = useContext(CryptodraftContext);

  const [paymentMethod, setPaymentMethod] = useState(MoreCollateralPaymentMethod.PGW);
  const [isSwap, setIsSwap] = useState(false);

  const coin = state?.selectedWallet?.coin;
  const fiatCurrency = state?.fiatCurrency;
  const cryptodraftSettings = state?.cryptodraftSettings;
  const screenOptions = state?.screenOptions;

  const rate = useRate(coin, fiatCurrency);
  const cryptoRate = useRate(fiatCurrency, coin);

  const [minDeposit, setMinDeposit] = useState(0);
  const [maxDeposit, setMaxDeposit] = useState(100);
  const [fiatCollateralInputWidth, setFiatCollateralInputWidth] = useState(0);
  const [collateralInputWidth, setCollateralInputWidth] = useState(0);
  const [collateralAmount, setCollateralAmount] = useState(0);
  const [collateralAmountDecimal, setCollateralAmountDecimal] = useState(0);
  const [fiatCollateralAmount, setFiatCollateralAmount] = useState(
    formatNumber(minDeposit * rate, exchange.FIAT_VALUE)
  );

  const [feauterPgwAllowed] = useSetting('app_features', 'feature_pgw_allowed');
  const [pgwAllowCryptoExchange] = useSetting(
    'payment_gateway_options',
    'pgw_allow_crypto_exchange'
  );

  const fiatCollateralInputSpan = useRef();
  const collateralInputSpan = useRef();
  const progressBarRef = useRef();

  const paymentMethods = useMemo(
    () => [
      // {
      //   key: MoreCollateralPaymentMethod.LEDGER,
      //   icon: 'buy-sell-menu',
      //   title: translate('ledger_wallet_title'),
      //   subtitle: translate('ledger_wallet_subtitle'),
      //   onChange: () => setPaymentMethod(MoreCollateralPaymentMethod.LEDGER),
      //   disabled: true,
      // },
      {
        key: MoreCollateralPaymentMethod.PGW,
        icon: 'outline-menu',
        title: translate('card_order_payment_card_input_title'),
        subtitle: translate('card_order_payment_card_input_subtitle'),
        onChange: () => setPaymentMethod(MoreCollateralPaymentMethod.PGW),
        disabled: !feauterPgwAllowed && !pgwAllowCryptoExchange,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feauterPgwAllowed, pgwAllowCryptoExchange]
  );

  useEffect(() => {
    if (cryptodraftSettings && state?.selectedWallet && state?.additionalAmountNeeded === null) {
      const minDepositRanges =
        cryptodraftSettings[`cryptodraft_minimum_initial_deposit_amount_${coin?.toUpperCase()}`];

      const maxDepositRanges =
        cryptodraftSettings[`cryptodraft_maximum_initial_deposit_amount_${coin?.toUpperCase()}`];

      setMinDeposit(minDepositRanges);
      setMaxDeposit(maxDepositRanges);
    } else if (state?.additionalAmountNeeded) {
      setMinDeposit(state?.additionalAmountNeeded);
    }
  }, [
    state?.selectedWallet,
    state?.additionalAmountNeeded,
    cryptodraftSettings,
    maxDeposit,
    minDeposit,
    coin,
  ]);

  useEffect(() => {
    setCollateralAmount(minDeposit);
    setCollateralAmountDecimal(minDeposit);
    setFiatCollateralAmount(formatNumber(minDeposit * rate, exchange.FIAT_VALUE));
  }, [minDeposit, rate]);

  useEffect(() => {
    const charactersLength =
      collateralInputSpan.current.value.length > 1 ? collateralInputSpan.current.value.length : 2;
    setCollateralInputWidth(charactersLength);
  }, [collateralAmount]);

  useEffect(() => {
    const charactersLength =
      fiatCollateralInputSpan.current.value.length > 1
        ? fiatCollateralInputSpan.current.value.length
        : 1;
    // to avoid having empty input, fix length to 1 if text length is less then 1
    setFiatCollateralInputWidth(charactersLength);
  }, [fiatCollateralAmount]);

  useEffect(() => {
    if (progressBarRef && progressBarRef?.current) {
      const newWidth = ((collateralAmountDecimal - minDeposit) * 100) / (maxDeposit - minDeposit);
      progressBarRef.current.style.width = `${newWidth > 0 ? newWidth : 0}%`;
    }
  }, [collateralAmountDecimal, progressBarRef, maxDeposit, minDeposit]);

  const handleChangeCollateralAmount = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(formattedAmount, state?.selectedWallet?.wallet_type)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiatCollateralAmount(0);
      setCollateralAmountDecimal(0);
      setCollateralAmount(0);
    }

    const calcFiat = formatNumber(decimalAmount * rate, exchange.FIAT_VALUE);
    const formattedFiatCalc = formatDecimalNumber(calcFiat);

    setFiatCollateralAmount(formattedFiatCalc.formattedAmount);
    setCollateralAmount(formattedAmount);
    setCollateralAmountDecimal(decimalAmount);
  };

  const handleChangeFiatAmount = (event) => {
    const { value: amount, min } = event?.target || event;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(decimalAmount, WalletsTypes.FIAT)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiatCollateralAmount(0);
      setCollateralAmount(0);
    }

    const calcCrypto = formatNumber(decimalAmount * cryptoRate, exchange.CRYPTO_VALUE);
    const formattedCalcCrypto = formatDecimalNumber(calcCrypto);

    setCollateralAmount(formattedCalcCrypto.formattedAmount);
    setCollateralAmountDecimal(formattedCalcCrypto.decimalAmount);
    setFiatCollateralAmount(formattedAmount);
  };

  const handleFocus = (e) => {
    if (!e.target) return;
    e.target.select();
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlurAmount = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  const onConfirmPayment = () => {
    if (paymentMethod === MoreCollateralPaymentMethod.PGW) {
      updatePgwRequestParams({
        currency: coin,
        amount: collateralAmountDecimal,
        displayCurrency: fiatCurrency,
      });
      openScreen(cryptodraftScreens.PROCEED_WITH_PAYMENT);
    } else if (paymentMethod !== MoreCollateralPaymentMethod.PGW) {
      // TODO: using LEDGER hardware wallet
    }
  };

  return (
    <MainBodyLayout
      withBack
      withInfoIcon
      onPrevClick={() =>
        openScreen(
          screenOptions?.openedFromInterestPayment
            ? cryptodraftScreens.INTEREST_PAYMENTS
            : cryptodraftScreens.CALCULATOR
        )
      }
      title={translate('cryptodraft_add_more_collateral_title')}>
      <p className="text-shadow text-f6 mt-4 pt-3">
        {translate('cryptodraft_your_collateral_label')}
      </p>
      <FocusableInputArea className="mt-3 mb-1">
        <input
          required
          max={!isSwap ? maxDeposit : ''}
          min={0}
          type="text"
          className="form-control bg-transparent p-0 text-shadow text-center border-0"
          value={isSwap ? fiatCollateralAmount : collateralAmount}
          style={{
            fontSize: '1rem',
            width: `${isSwap ? fiatCollateralInputWidth : collateralInputWidth + 1}ch`,
            height: '3rem',
          }}
          ref={isSwap ? fiatCollateralInputSpan : collateralInputSpan}
          onChange={(e) => (isSwap ? handleChangeFiatAmount(e) : handleChangeCollateralAmount(e))}
          placeholder="0 "
        />
        <span
          style={{ fontSize: 'inherit', pointerEvents: 'none' }}
          className="text-f5 text-shadow">
          {isSwap ? fiatCurrency?.toUpperCase() : coin?.toUpperCase()}
        </span>
        <span
          role="button"
          className="mb-1"
          tabIndex="0"
          onKeyPress={() => setIsSwap((prev) => !prev)}
          onClick={() => setIsSwap((prev) => !prev)}>
          <img src="./assets/images/switch.svg" className="ms-2" alt="switch" />
        </span>
      </FocusableInputArea>

      <FocusableInputArea className="h1 amount-input">
        <input
          required
          max={isSwap ? maxDeposit : ''}
          min="0"
          type="text"
          className="form-control bg-transparent text-bold p-0 text-center border-0"
          value={isSwap ? collateralAmount : fiatCollateralAmount}
          style={{
            fontSize: 'inherit',
            width: `${isSwap ? collateralInputWidth : fiatCollateralInputWidth + 1}ch`,
            color: 'inherit',
            height: 'inherit',
            textIndent: collateralInputWidth || fiatCollateralInputWidth ? 0 : '34%',
          }}
          ref={isSwap ? collateralInputSpan : fiatCollateralInputSpan}
          onChange={(e) => (isSwap ? handleChangeCollateralAmount(e) : handleChangeFiatAmount(e))}
          onFocus={handleFocus}
          onBlur={handleBlurAmount}
          placeholder="0  "
        />
        <b
          style={{
            fontSize: 'inherit',
            pointerEvents: 'none',
            color: 'inherit',
          }}>
          {isSwap ? coin?.toUpperCase() : fiatCurrency?.toUpperCase()}
        </b>
      </FocusableInputArea>
      <div className="mb-4 position-relative">
        <div className="slider-progress">
          <div className="progress">
            <div className="progress-bar" ref={progressBarRef} />
          </div>
          <input
            type="range"
            className="form-range"
            value={collateralAmountDecimal}
            max={maxDeposit}
            min={minDeposit}
            step="0.0001"
            onChange={(e) => {
              handleChangeCollateralAmount(e);
            }}
          />
        </div>
        {/* this lines for implementing edges for the slider */}
        <div
          className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start"
          style={{ top: '15%' }}>
          {Array.from({ length: 7 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 1 || item === 7 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
        </div>
        <div className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start">
          <p className="text-f6 text-disabled">
            {`${formatNumberWithComma(
              formatNumber(minDeposit, exchange.FIAT_VALUE)
            )} ${coin?.toUpperCase()}`}
          </p>
          <p className="text-f6 text-disabled">
            {`${formatNumberWithComma(
              formatNumber(maxDeposit, exchange.FIAT_VALUE)
            )} ${coin?.toUpperCase()}`}
          </p>
        </div>
      </div>
      <div className="mt-4 pb-1">
        <p className="text-f6 text-shadow">{translate('card_order_select_payment_title')}</p>
      </div>
      {paymentMethods.map((item) => (
        <RadioItem
          backgroundColor="bg-lighter"
          className="mt-2"
          key={item.key}
          label={item.title}
          name="paymentMethod"
          onChange={item.onChange}
          checked={paymentMethod === item.key}
          icon={item.icon}
          subtitle={item.subtitle}
          disabled={item.disabled}
        />
      ))}
      <div className="btn-container">
        <button
          disabled={collateralAmountDecimal <= 0}
          type="button"
          className="btn py-3 btn-primary w-100"
          onClick={onConfirmPayment}>
          {translate('continue_btn_title')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default AddCollateral;
